// Tolerant search utilities (client-side fuzzy + accent-insensitive).

export function normalise(s) {
  if (!s) return "";
  return String(s).normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

// Tokenise free-text query into terms (>= 1 char each).
export function tokenise(q) {
  return normalise(q).split(/\s+/).filter(Boolean);
}

// Levenshtein-ish similarity for short strings. Used only for fuzzy AUTOCOMPLETE,
// not main matching (which uses substring + token coverage).
export function tinyDistance(a, b) {
  if (a === b) return 0;
  if (!a || !b) return Math.max(a?.length || 0, b?.length || 0);
  if (a.length > b.length) [a, b] = [b, a];
  let prev = Array.from({ length: a.length + 1 }, (_, i) => i);
  for (let j = 1; j <= b.length; j++) {
    const curr = [j];
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr.push(Math.min(prev[i] + 1, curr[i - 1] + 1, prev[i - 1] + cost));
    }
    prev = curr;
  }
  return prev[a.length];
}

// Score 0..1 — higher is better. Term coverage + position bonus.
export function scoreMatch(text, query) {
  const t = normalise(text);
  const terms = tokenise(query);
  if (!t || !terms.length) return 0;
  let coverage = 0;
  let positionBonus = 0;
  for (const term of terms) {
    const idx = t.indexOf(term);
    if (idx >= 0) {
      coverage++;
      positionBonus += idx === 0 ? 0.3 : idx < 6 ? 0.15 : 0.05;
    } else {
      // Soft fuzzy fallback for short terms (≤ 5 chars)
      if (term.length <= 5 && tinyDistance(term, t.slice(0, Math.min(t.length, term.length + 2))) <= 1) {
        coverage += 0.5;
      }
    }
  }
  return Math.min(1, (coverage / terms.length) * 0.9 + positionBonus);
}

// Known catalog synonyms (Eiffel 65 specific).
export const SYNONYMS = {
  "da ba dee": "blue",
  "dabadi":    "blue",
  "dabadee":   "blue",
  "blu":       "blue",
  "eiffel blue": "blue",
  "europop":   "europop blue",
  "65":        "eiffel 65",
};

// Apply synonym expansion to a query.
export function expandQuery(q) {
  const n = normalise(q);
  let expanded = n;
  for (const key of Object.keys(SYNONYMS)) {
    if (n.includes(key)) expanded += " " + SYNONYMS[key];
  }
  return expanded;
}
